// 把壳层的 store 与服务接到 SDK 运行时表面上，
// 这样 APP 才能调用 @blade/os-sdk 暴露的顶层函数。
import type { Permission, SdkRuntime } from '@blade/os-sdk';
import { installRuntime } from '@blade/os-sdk';
import type { Registry } from '../apps/registryContext';
import { useDesktopLayoutStore } from '../store/desktopLayoutStore';
import { useSessionStore } from '../store/sessionStore';
import { useWindowStore } from '../store/windowStore';
import { pushNotification } from '../store/notificationStore';
import { http } from './http';
import type { LaunchAppFn } from './launchApp';

export interface SdkRuntimeDeps {
  launchApp: LaunchAppFn;
  registry: Registry;
  refreshRegistry: () => Promise<void>;
}

export function installSdkRuntime(deps: SdkRuntimeDeps): void {
  const runtime: SdkRuntime = {
    http,
    launchApp: (appId, options) => deps.launchApp(appId, options),
    closeWindow: (windowId) => {
      const targetId = windowId ?? useWindowStore.getState().focusedId;
      if (!targetId) return;
      useWindowStore.getState().close(targetId);
    },
    notify: (payload) => pushNotification(payload),
    currentUser: () => {
      const u = useSessionStore.getState().user;
      if (!u) return null;
      return u;
    },
    updateCurrentUser: (user) => {
      const csrfToken = useSessionStore.getState().csrfToken;
      if (!csrfToken) return;
      useSessionStore.getState().setAuthenticated(user, csrfToken);
    },
    switchLocale: (_locale) => {
      // i18n 相关接线后续再补；先把 SDK 表面稳定住，避免 APP 侧接口反复变化。
    },
    t: (key) => key,
    refreshRegistry: () => deps.refreshRegistry(),
    refreshDesktopLayout: async () => {
      // 顺序契约见 desktopLayoutStore.flush() 注释:
      //   先把本地 pending 修改 flush,失败 → return(不刷掉本地);
      //   成功 → load 拉新布局,load 失败仅通知不阻断 caller。
      const store = useDesktopLayoutStore.getState();
      try {
        await store.flush();
      } catch {
        return;
      }
      try {
        await store.load();
      } catch {
        // load 内部已 pushNotification
      }
    },
    hasPermission: (permission: Permission) => {
      // SDK 的 hasPermission() 签名里没有 windowId / appId 参数
      //（design.md §4.5），因此这里通过当前聚焦窗口反推调用者所属的 APP。
      // 如果 APP 需要检查某个特定窗口的权限，应改用 closeWindow / useWindowContext。
      const focusedId = useWindowStore.getState().focusedId;
      if (!focusedId) return false;
      const win = useWindowStore
        .getState()
        .windows.find((w) => w.id === focusedId);
      if (!win) return false;
      const entry = deps.registry.get(win.appId);
      return entry?.permissions.includes(permission) ?? false;
    },
  };
  installRuntime(runtime);
}
